import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { CMS_POST_API_CONFIG } from '../config';
import { ApiResponse, PaginatedResponse } from '../models';
import {
  AudioSeries,
  AudioSeriesRoute,
  CreateAudioSeriesDto,
  UpdateAudioSeriesDto,
  AddAudioEpisodeDto,
  ReorderAudioEpisodesDto,
  AudioSeriesListParams,
} from '../models/audio-series.model';

@Injectable({ providedIn: 'root' })
export class AudioSeriesService {
  private readonly _http = inject(HttpClient);
  private readonly _apiBase: string;

  private readonly _readerBase: string;

  constructor() {
    const config = inject(CMS_POST_API_CONFIG);
    this._apiBase = `${config.apiBaseUrl}/api/audio-series`;
    this._readerBase = `${config.apiBaseUrl}/api/reader/audio-series`;
  }

  // ─── Public (unauthenticated) ──────────────────────────────────────────────

  getPublicAudioSeries(
    params: Omit<AudioSeriesListParams, 'publishStatus'>
  ): Observable<PaginatedResponse<AudioSeries>> {
    const httpParams = new HttpParams({ fromObject: this._toStringParams(params as Record<string, unknown>) });
    return this._http.get<PaginatedResponse<AudioSeries>>(`${this._apiBase}/public`, { params: httpParams });
  }

  getPublicAudioSeriesBySlug(slug: string, orgId: string): Observable<ApiResponse<AudioSeries>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<AudioSeries>>(`${this._apiBase}/public/${slug}`, { params: httpParams });
  }

  getPublicAudioSeriesRoutes(orgId: string): Observable<ApiResponse<AudioSeriesRoute[]>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<AudioSeriesRoute[]>>(`${this._apiBase}/public/routes`, { params: httpParams });
  }

  // ─── Authenticated ─────────────────────────────────────────────────────────

  listAudioSeries(params: AudioSeriesListParams): Observable<PaginatedResponse<AudioSeries>> {
    const httpParams = new HttpParams({ fromObject: this._toStringParams(params as Record<string, unknown>) });
    return this._http.get<PaginatedResponse<AudioSeries>>(this._apiBase, { params: httpParams });
  }

  getAudioSeriesById(id: string, orgId: string): Observable<ApiResponse<AudioSeries>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<AudioSeries>>(`${this._apiBase}/${id}`, { params: httpParams });
  }

  createAudioSeries(dto: CreateAudioSeriesDto): Observable<ApiResponse<AudioSeries>> {
    return this._http.post<ApiResponse<AudioSeries>>(this._apiBase, dto);
  }

  updateAudioSeries(id: string, dto: UpdateAudioSeriesDto): Observable<ApiResponse<AudioSeries>> {
    return this._http.patch<ApiResponse<AudioSeries>>(`${this._apiBase}/${id}`, dto);
  }

  publishAudioSeries(
    id: string,
    publishStatus: 'draft' | 'internal' | 'public' = 'public'
  ): Observable<ApiResponse<AudioSeries>> {
    return this._http.patch<ApiResponse<AudioSeries>>(`${this._apiBase}/${id}/publish`, { publishStatus });
  }

  deleteAudioSeries(id: string, orgId: string): Observable<{ success: boolean; message: string }> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.delete<{ success: boolean; message: string }>(
      `${this._apiBase}/${id}`,
      { params: httpParams }
    );
  }

  // ─── Episodes ─────────────────────────────────────────────────────────────

  addEpisode(seriesId: string, dto: AddAudioEpisodeDto): Observable<ApiResponse<AudioSeries>> {
    return this._http.post<ApiResponse<AudioSeries>>(`${this._apiBase}/${seriesId}/episodes`, dto);
  }

  removeEpisode(seriesId: string, episodeId: string, orgId: string): Observable<ApiResponse<AudioSeries>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.delete<ApiResponse<AudioSeries>>(
      `${this._apiBase}/${seriesId}/episodes/${episodeId}`,
      { params: httpParams }
    );
  }

  reorderEpisodes(seriesId: string, dto: ReorderAudioEpisodesDto): Observable<ApiResponse<AudioSeries>> {
    return this._http.patch<ApiResponse<AudioSeries>>(
      `${this._apiBase}/${seriesId}/episodes/reorder`,
      dto
    );
  }
  // ─── Reader (authenticated, visibility-filtered) ───────────────────────────

  getReaderAudioSeries(orgId: string, page?: number, limit?: number): Observable<PaginatedResponse<AudioSeries>> {
    let params = new HttpParams().set('orgId', orgId);
    if (page !== undefined) params = params.set('page', String(page));
    if (limit !== undefined) params = params.set('limit', String(limit));
    return this._http.get<PaginatedResponse<AudioSeries>>(this._readerBase, { params });
  }

  getReaderAudioSeriesBySlug(slug: string, orgId: string): Observable<ApiResponse<AudioSeries>> {
    const params = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<AudioSeries>>(`${this._readerBase}/${slug}`, { params });
  }
  // ─── Helpers ──────────────────────────────────────────────────────────────

  private _toStringParams(obj: Record<string, unknown>): Record<string, string> {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, String(v)])
    );
  }
}
